import React from "react";
import {
  User,
  Users,
  ShieldCheck,
  Check,
  Circle,
  MapPin,
  Receipt,
  Home,
  Baby,
  Star,
  Building2,
  Stethoscope,
  Signal,
  Wifi,
  BatteryFull,
} from "lucide-react";

// =============================================================================
// PHONE FRAME  —  clean, code-rendered digital mockup shell
// =============================================================================
const PhoneFrame = ({ children, testId }) => (
  <div
    className="mx-auto w-full max-w-[280px] rounded-[38px] p-1.5"
    data-testid={testId}
    style={{
      background: "linear-gradient(160deg, #10233C 0%, #04111F 100%)",
      border: "1px solid rgba(255,255,255,0.15)",
      boxShadow:
        "0 30px 60px -30px rgba(0,0,0,0.60), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.10)",
    }}
  >
    <div
      className="rounded-[32px] overflow-hidden relative"
      style={{
        background:
          "linear-gradient(180deg, #08182B 0%, #04111F 100%)",
      }}
    >
      {/* Dynamic island / notch */}
      <div className="relative h-6 flex items-center justify-center">
        <div
          aria-hidden="true"
          className="w-16 h-3.5 rounded-full"
          style={{
            background: "#04111F",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        />
      </div>
      {/* Status bar */}
      <div className="flex items-center justify-between px-5 pb-1.5">
        <span
          className="text-[11px] font-semibold text-white"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          9:41
        </span>
        <div className="flex items-center gap-1 text-white/85">
          <Signal size={11} strokeWidth={2.5} />
          <Wifi size={11} strokeWidth={2.5} />
          <BatteryFull size={13} strokeWidth={2.5} />
        </div>
      </div>
      <div className="px-4 pt-1 pb-4">{children}</div>
      {/* Home indicator */}
      <div className="flex items-center justify-center pb-2 pt-1">
        <div
          aria-hidden="true"
          className="w-24 h-1 rounded-full"
          style={{ background: "rgba(255,255,255,0.35)" }}
        />
      </div>
    </div>
  </div>
);

// -------------------- Small mockup primitives --------------------
const TopPill = ({ label, tone = "green" }) => (
  <div
    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-[0.18em]"
    style={
      tone === "purple"
        ? {
            background: "rgba(183,165,255,0.14)",
            border: "1px solid rgba(183,165,255,0.40)",
            color: "#B7A5FF",
          }
        : {
            background: "rgba(34,197,133,0.14)",
            border: "1px solid rgba(34,197,133,0.45)",
            color: "#22C585",
          }
    }
  >
    <span
      className="w-1 h-1 rounded-full"
      style={{
        background: tone === "purple" ? "#B7A5FF" : "#22C585",
        boxShadow:
          tone === "purple"
            ? "0 0 8px rgba(183,165,255,0.85)"
            : "0 0 8px rgba(34,197,133,0.85)",
      }}
    />
    {label}
  </div>
);

const ScreenTitle = ({ children }) => (
  <div
    className="mt-2 text-[15px] font-extrabold text-white leading-tight"
    style={{ fontFamily: "Sora, Inter, sans-serif" }}
  >
    {children}
  </div>
);

const MiniCard = ({ children, style, className = "" }) => (
  <div
    className={`rounded-xl p-2.5 ${className}`}
    style={{
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.10)",
      ...style,
    }}
  >
    {children}
  </div>
);

// =============================================================================
// SIX DIGITAL SCREEN MOCKUPS
// =============================================================================

// --- ALEX 01 — TDY Invoicing / per-diem tracking ---
const MockInvoicing = () => (
  <PhoneFrame testId="mock-alex-invoicing">
    <TopPill label="TDY Invoicing" />
    <ScreenTitle>Per-diem locked.</ScreenTitle>
    <MiniCard
      className="mt-3"
      style={{
        background:
          "linear-gradient(160deg, rgba(34,197,133,0.22) 0%, rgba(34,197,133,0.06) 100%)",
        border: "1px solid rgba(34,197,133,0.45)",
      }}
    >
      <div className="text-[8px] uppercase tracking-[0.20em] text-[#22C585] font-bold">
        Safe to Spend · TDY
      </div>
      <div
        className="mt-0.5 text-[22px] font-extrabold text-white leading-none"
        style={{ fontFamily: "Sora, Inter, sans-serif" }}
      >
        $487<span className="text-white/60 text-[13px]">.00</span>
      </div>
      <div className="mt-1 text-[9px] text-white/70">14-day TDY window</div>
    </MiniCard>
    <div className="mt-2.5 space-y-1.5">
      {[
        { k: "Lodging cap", v: "$124/day" },
        { k: "M&IE per-diem", v: "$59/day" },
        { k: "Incidentals", v: "$5/day" },
        { k: "DTS invoice", v: "#TDY-4471" },
      ].map((r) => (
        <div
          key={r.k}
          className="flex items-center justify-between text-[10px]"
        >
          <span className="text-white/70">{r.k}</span>
          <span className="text-white font-semibold">{r.v}</span>
        </div>
      ))}
    </div>
    <div
      className="mt-3 flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[9px] font-bold"
      style={{
        background: "rgba(34,197,133,0.10)",
        border: "1px solid rgba(34,197,133,0.35)",
        color: "#22C585",
      }}
    >
      <Check size={11} strokeWidth={3} />
      Tracked to Nerve Center
    </div>
  </PhoneFrame>
);

// --- ALEX 02 — Tactical Road Map (JTR route, 8-hour increments) ---
const MockRouteMap = () => (
  <PhoneFrame testId="mock-alex-routemap">
    <TopPill label="Route Active" />
    <ScreenTitle>Fort Bragg → School</ScreenTitle>
    <div
      className="mt-3 relative rounded-xl overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 30% 40%, rgba(34,197,133,0.20) 0%, rgba(34,197,133,0) 55%), linear-gradient(180deg, #071527 0%, #04111F 100%)",
        border: "1px solid rgba(255,255,255,0.10)",
        aspectRatio: "1 / 1",
      }}
    >
      {/* Grid */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "18px 18px",
        }}
      />
      {/* Route SVG */}
      <svg
        viewBox="0 0 200 200"
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="none"
      >
        <path
          d="M20,160 C60,150 70,110 100,105 C130,100 140,60 180,45"
          stroke="#22C585"
          strokeWidth="2.5"
          strokeDasharray="5 4"
          fill="none"
          strokeLinecap="round"
        />
        {[
          { x: 20, y: 160, label: "BRG" },
          { x: 100, y: 105, label: "" },
          { x: 180, y: 45, label: "SCH" },
        ].map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="6" fill="#22C585" />
            <circle
              cx={p.x}
              cy={p.y}
              r="10"
              fill="#22C585"
              opacity="0.25"
            />
          </g>
        ))}
      </svg>
      <div className="absolute top-2 left-2 text-[8px] uppercase tracking-[0.18em] text-white/60 font-bold">
        JTR Route · Active
      </div>
      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
        <div className="text-[9px] text-white font-semibold">Fort Bragg, NC</div>
        <div className="text-[9px] text-[#22C585] font-bold">1,562 mi</div>
      </div>
    </div>
    <div className="mt-2.5 grid grid-cols-3 gap-1.5">
      <MiniCard className="!p-1.5 text-center">
        <div className="text-[8px] text-white/60 uppercase tracking-widest">
          Legs
        </div>
        <div
          className="text-[11px] font-extrabold text-white"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          3
        </div>
      </MiniCard>
      <MiniCard className="!p-1.5 text-center">
        <div className="text-[8px] text-white/60 uppercase tracking-widest">
          Max/day
        </div>
        <div
          className="text-[11px] font-extrabold text-white"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          8 hr
        </div>
      </MiniCard>
      <MiniCard className="!p-1.5 text-center">
        <div className="text-[8px] text-white/60 uppercase tracking-widest">
          Lodging
        </div>
        <div
          className="text-[11px] font-extrabold text-[#22C585]"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          JTR
        </div>
      </MiniCard>
    </div>
  </PhoneFrame>
);

// --- ALEX 03 — Voucher Vault (receipts + Preparation Worksheet) ---
const MockVoucherVault = () => (
  <PhoneFrame testId="mock-alex-voucher">
    <TopPill label="Voucher Vault" />
    <ScreenTitle>Vouchers, built.</ScreenTitle>
    <div className="mt-3 space-y-1.5">
      {[
        { k: "Applebees · Meal", v: "$12.40" },
        { k: "Marriott · Lodging", v: "$124.00" },
        { k: "Shell · Fuel", v: "$42.10" },
      ].map((r) => (
        <MiniCard key={r.k} className="!p-2 flex items-center gap-2">
          <div
            className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
            style={{
              background: "rgba(34,197,133,0.15)",
              border: "1px solid rgba(34,197,133,0.40)",
            }}
          >
            <Receipt size={11} className="text-[#22C585]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-white font-semibold truncate">
              {r.k}
            </div>
            <div className="text-[8px] text-white/60">Scanned · verified</div>
          </div>
          <div className="text-[10px] text-white font-bold shrink-0">{r.v}</div>
          <Check size={11} className="text-[#22C585] shrink-0" strokeWidth={3} />
        </MiniCard>
      ))}
    </div>
    <MiniCard
      className="mt-3"
      style={{
        background:
          "linear-gradient(160deg, rgba(34,197,133,0.20) 0%, rgba(34,197,133,0.06) 100%)",
        border: "1px solid rgba(34,197,133,0.45)",
      }}
    >
      <div className="text-[8px] uppercase tracking-[0.18em] text-[#22C585] font-bold">
        Voucher Preparation Worksheet
      </div>
      <div className="mt-1 flex items-center justify-between">
        <div
          className="text-[13px] font-extrabold text-white"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          12 of 14 receipts
        </div>
        <div className="text-[9px] text-[#22C585] font-bold">86%</div>
      </div>
      <div
        className="mt-1.5 w-full h-1.5 rounded-full overflow-hidden"
        style={{ background: "rgba(255,255,255,0.10)" }}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: "86%",
            background:
              "linear-gradient(90deg, #22C585 0%, #7AF0BA 100%)",
          }}
        />
      </div>
    </MiniCard>
  </PhoneFrame>
);

// --- O'HARA 01 — Clearing Fort Cavazos (checklist) ---
const MockClearingCavazos = () => (
  <PhoneFrame testId="mock-ohara-clearing">
    <TopPill label="Clear-Out" />
    <ScreenTitle>Clearing Cavazos.</ScreenTitle>
    <MiniCard className="mt-3">
      <div className="text-[8px] uppercase tracking-[0.18em] text-white/60 font-bold">
        Out-Processing Progress
      </div>
      <div className="mt-1 flex items-center justify-between">
        <div
          className="text-[13px] font-extrabold text-white"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          2 of 5 tasks
        </div>
        <div className="text-[9px] text-[#22C585] font-bold">40%</div>
      </div>
      <div
        className="mt-1.5 w-full h-1.5 rounded-full overflow-hidden"
        style={{ background: "rgba(255,255,255,0.10)" }}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: "40%",
            background:
              "linear-gradient(90deg, #22C585 0%, #7AF0BA 100%)",
          }}
        />
      </div>
    </MiniCard>
    <div className="mt-2.5 space-y-1.5">
      {[
        { done: true, k: "Utilities scheduled", d: "Sep 08" },
        { done: true, k: "TMO pickup confirmed", d: "Sep 12" },
        { done: false, k: "Out-process CIF", d: "Sep 15" },
        { done: false, k: "Final PCS brief", d: "Sep 18" },
        { done: false, k: "Household ledger export", d: "Sep 19" },
      ].map((r) => (
        <div key={r.k} className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
            style={
              r.done
                ? {
                    background: "#22C585",
                    boxShadow: "0 0 8px rgba(34,197,133,0.5)",
                  }
                : {
                    background: "transparent",
                    border: "1.5px solid rgba(255,255,255,0.30)",
                  }
            }
          >
            {r.done ? (
              <Check size={10} strokeWidth={3} className="text-[#04111F]" />
            ) : null}
          </div>
          <div className="flex-1 min-w-0 flex items-center justify-between">
            <span
              className={`text-[10px] truncate ${r.done ? "text-white/60 line-through" : "text-white font-semibold"}`}
            >
              {r.k}
            </span>
            <span className="text-[9px] text-white/50 ml-2 shrink-0">
              {r.d}
            </span>
          </div>
        </div>
      ))}
    </div>
  </PhoneFrame>
);

// --- O'HARA 02 — Advanced Recon (BAH housing + certified daycare) ---
const MockAdvancedRecon = () => (
  <PhoneFrame testId="mock-ohara-recon">
    <TopPill label="Advanced Recon" />
    <ScreenTitle>BAH-friendly · Certified.</ScreenTitle>
    <div className="mt-3 space-y-1.5">
      {[
        { icon: Home, k: "3BR Townhome", d: "Hinesville, GA", p: "$2,100", ok: true },
        { icon: Home, k: "4BR Family Home", d: "Fort Stewart, GA", p: "$2,450", ok: true },
      ].map((r) => (
        <MiniCard key={r.k} className="!p-2 flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
            style={{
              background: "rgba(34,197,133,0.15)",
              border: "1px solid rgba(34,197,133,0.40)",
            }}
          >
            <r.icon size={13} className="text-[#22C585]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-white font-semibold truncate">
              {r.k}
            </div>
            <div className="text-[8px] text-white/60 truncate">{r.d}</div>
          </div>
          <div className="text-[10px] text-white font-bold shrink-0">{r.p}</div>
          <div
            className="px-1.5 py-0.5 rounded-md text-[7px] font-extrabold uppercase tracking-wider shrink-0"
            style={{
              background: "rgba(34,197,133,0.18)",
              border: "1px solid rgba(34,197,133,0.45)",
              color: "#22C585",
            }}
          >
            BAH
          </div>
        </MiniCard>
      ))}
    </div>
    <MiniCard className="mt-2.5 !p-2 flex items-center gap-2">
      <div
        className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
        style={{
          background: "rgba(183,165,255,0.15)",
          border: "1px solid rgba(183,165,255,0.40)",
        }}
      >
        <Baby size={13} className="text-[#B7A5FF]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] text-white font-semibold truncate">
          Fort Stewart CDC
        </div>
        <div className="text-[8px] text-white/60">Daycare · Verified spot</div>
      </div>
      <Check size={11} className="text-[#22C585] shrink-0" strokeWidth={3} />
    </MiniCard>
    <MiniCard className="mt-1.5 !p-2 flex items-center gap-2">
      <div
        className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
        style={{
          background: "rgba(34,197,133,0.15)",
          border: "1px solid rgba(34,197,133,0.40)",
        }}
      >
        <Building2 size={13} className="text-[#22C585]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] text-white font-semibold truncate">
          Walker Elementary
        </div>
        <div className="text-[8px] text-white/60">Certified · 2.1 mi</div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <Star size={9} className="text-[#F4C752]" fill="#F4C752" />
        <span className="text-[9px] text-white font-bold">4.6</span>
      </div>
    </MiniCard>
  </PhoneFrame>
);

// --- O'HARA 03 — Arrival at Fort Stewart Welcome Center (Life Profile) ---
const MockArrival = () => (
  <PhoneFrame testId="mock-ohara-arrival">
    <TopPill label="Life Profile" />
    <ScreenTitle>Fort Stewart, GA</ScreenTitle>
    <MiniCard className="mt-3">
      <div className="flex items-center gap-2">
        <MapPin size={12} className="text-[#22C585]" />
        <div className="text-[9px] text-white/70 uppercase tracking-widest">
          Duty Station · 5 mi radius
        </div>
      </div>
      <div
        className="mt-1 text-[12px] font-extrabold text-white"
        style={{ fontFamily: "Sora, Inter, sans-serif" }}
      >
        Welcome home.
      </div>
    </MiniCard>
    <div className="mt-2.5 space-y-1.5">
      {[
        {
          icon: Building2,
          title: "Walker Elementary",
          sub: "Public K-5 · 412 students",
          badge: "4.6",
          color: "#22C585",
          bg: "rgba(34,197,133,0.15)",
          border: "rgba(34,197,133,0.40)",
        },
        {
          icon: Stethoscope,
          title: "Winn Army Hospital",
          sub: "TRICARE · 3.4 mi",
          badge: "★",
          color: "#EF6B6B",
          bg: "rgba(239,107,107,0.15)",
          border: "rgba(239,107,107,0.40)",
        },
        {
          icon: Baby,
          title: "Fort Stewart CDC",
          sub: "Daycare · Confirmed",
          badge: "✓",
          color: "#B7A5FF",
          bg: "rgba(183,165,255,0.15)",
          border: "rgba(183,165,255,0.40)",
        },
      ].map((r) => (
        <MiniCard key={r.title} className="!p-2 flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
            style={{
              background: r.bg,
              border: `1px solid ${r.border}`,
            }}
          >
            <r.icon size={13} style={{ color: r.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-white font-semibold truncate">
              {r.title}
            </div>
            <div className="text-[8px] text-white/60 truncate">{r.sub}</div>
          </div>
          <div
            className="px-1.5 py-0.5 rounded-md text-[9px] font-extrabold shrink-0"
            style={{ background: r.bg, color: r.color, border: `1px solid ${r.border}` }}
          >
            {r.badge}
          </div>
        </MiniCard>
      ))}
    </div>
  </PhoneFrame>
);

// =============================================================================
// TIMELINE STEP  —  numbered dot + solid green rail + digital mockup beside
// =============================================================================
const TimelineStep = ({ index, isLast, label, title, description, mock, testId }) => (
  <div
    className="relative grid grid-cols-[44px_1fr] md:grid-cols-[52px_1fr] gap-4 md:gap-5"
    data-testid={testId}
  >
    {/* LEFT RAIL */}
    <div className="relative flex flex-col items-center">
      <div
        className="relative z-10 w-11 h-11 md:w-13 md:h-13 rounded-full flex items-center justify-center font-extrabold text-[#04111F] shrink-0"
        style={{
          background: "#22C585",
          boxShadow:
            "0 12px 30px -12px rgba(34,197,133,0.75), inset 0 1px 0 rgba(255,255,255,0.35)",
          fontFamily: "Sora, Inter, sans-serif",
          fontSize: 18,
          width: 44,
          height: 44,
        }}
      >
        {index}
      </div>
      {!isLast && (
        <div
          className="flex-1 w-[2px] mt-1"
          style={{
            background: "#22C585",
            opacity: 0.7,
            boxShadow: "0 0 12px rgba(34,197,133,0.35)",
          }}
        />
      )}
    </div>

    {/* RIGHT CONTENT */}
    <div className={`${isLast ? "pb-0" : "pb-6 md:pb-8"}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#22C585] font-bold">
        {label}
      </div>
      <h4
        className="mt-1.5 text-lg md:text-xl font-extrabold text-white leading-tight"
        style={{ fontFamily: "Sora, Inter, sans-serif" }}
      >
        {title}
      </h4>
      <p className="mt-1.5 text-[13.5px] md:text-[14px] text-[#C7D0DF] leading-relaxed">
        {description}
      </p>
      <div className="mt-3">{mock}</div>
    </div>
  </div>
);

const PersonaHeader = ({ icon: Icon, rank, name, subtitle, headline, scenario, testId }) => (
  <div className="mb-6" data-testid={testId}>
    <div className="flex items-center gap-4">
      <div className="relative shrink-0">
        <div
          className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center overflow-hidden"
          style={{
            background:
              "linear-gradient(140deg, rgba(34,197,133,0.22) 0%, rgba(34,197,133,0.06) 100%)",
            border: "1px solid rgba(34,197,133,0.55)",
            boxShadow:
              "0 20px 40px -20px rgba(34,197,133,0.35), inset 0 1px 0 rgba(255,255,255,0.10)",
          }}
        >
          <Icon size={28} className="text-[#22C585]" />
        </div>
        <div
          className="absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded-md text-[9px] font-extrabold tracking-widest"
          style={{
            background: "#04111F",
            border: "1px solid rgba(34,197,133,0.55)",
            color: "#22C585",
            fontFamily: "Sora, Inter, sans-serif",
          }}
        >
          {rank}
        </div>
      </div>
      <div className="min-w-0">
        <div
          className="text-lg md:text-xl font-extrabold text-white leading-tight"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          {name}
        </div>
        <div className="mt-0.5 text-[11px] text-[#A8B0BE] uppercase tracking-[0.18em]">
          {subtitle}
        </div>
      </div>
    </div>

    <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-[#22C585] font-bold">
      {headline}
    </div>
    <p className="mt-1.5 text-[14px] text-[#C7D0DF] leading-relaxed">{scenario}</p>
  </div>
);

const StoryPanel = ({ testId, header, steps, footerCallout }) => (
  <div
    className="rounded-3xl p-6 md:p-7 relative overflow-hidden h-full flex flex-col"
    data-testid={testId}
    style={{
      background:
        "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow:
        "0 40px 80px -40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
    }}
  >
    <div
      aria-hidden="true"
      className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-20 blur-3xl"
      style={{ background: "#22C585" }}
    />
    <div className="relative flex-1">
      {header}
      <div className="mt-2">
        {steps.map((step, i) => (
          <TimelineStep
            key={step.title}
            index={i + 1}
            isLast={i === steps.length - 1}
            {...step}
            testId={`${testId}-step-${i + 1}`}
          />
        ))}
      </div>
      {footerCallout}
    </div>
  </div>
);

const MissionStories = () => {
  return (
    <section
      id="mission-stories"
      className="relative py-14 md:py-20"
      data-testid="mission-stories-section"
    >
      <div className="mx-auto max-w-[1200px] px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#22C585]">
            Mission Stories
          </div>
          <h2
            className="mt-3 text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight"
            style={{ fontFamily: "Sora, Inter, sans-serif" }}
            data-testid="mission-stories-title"
          >
            Two service members. One financial Nerve Center.
          </h2>
          <p className="mt-3 text-[15px] text-[#C7D0DF] leading-relaxed">
            Follow the mission end-to-end — every step, every phase, one Nerve Center.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:gap-8 lg:grid-cols-2 items-stretch">
          {/* STORY 1 — PFC Alex, TDY 3-Step Timeline */}
          <StoryPanel
            testId="story-card-1"
            header={
              <PersonaHeader
                icon={User}
                rank="PFC"
                name="PFC Alex"
                subtitle="Single Soldier · TDY Focus"
                headline="The TDY Mission"
                scenario="Driving 24+ hours from Fort Bragg to a school for TDY orders. Different city, different mission, same Nerve Center in his pocket."
                testId="story-1-header"
              />
            }
            steps={[
              {
                label: "Step 01 · Invoicing",
                title: "Track TDY per-diem & DTS invoicing",
                description:
                  "Alex logs into SpendNova the moment orders drop. Per-diem, lodging cap, and DTS invoice figures line up beside his real balance so he knows exactly what's safe to spend before he ever hits the road.",
                mock: <MockInvoicing />,
              },
              {
                label: "Step 02 · Tactical Road Map",
                title: "JTR route, 8-hour driving increments",
                description:
                  "The Nerve Center plots a JTR-informed route in 8-hour driving increments, marks lodging planning options, and routes fuel stops so Alex stays on-mission without stretching regulations.",
                mock: <MockRouteMap />,
              },
              {
                label: "Step 03 · Lodging & Voucher Vault",
                title: "Lodging locked, Voucher Vault building itself",
                description:
                  "Every hotel folio and meal receipt drops straight into the Voucher Vault. When class ends, Alex's Voucher Preparation Worksheet is already built — no paperwork nightmare waiting back at his unit.",
                mock: <MockVoucherVault />,
              },
            ]}
          />

          {/* STORY 2 — SSG O'Hara, PCS 3-Phase Lifecycle */}
          <StoryPanel
            testId="story-card-2"
            header={
              <PersonaHeader
                icon={Users}
                rank="SSG"
                name="SSG O'Hara"
                subtitle="E-6 with Dependents · Family PCS"
                headline="The PCS Lifecycle"
                scenario="PCSing from Fort Cavazos to Fort Stewart with her husband, her mother, and her twins. Housing, schools, hospitals — all mapped before the moving truck rolls."
                testId="story-2-header"
              />
            }
            steps={[
              {
                label: "Phase 01 · Clearing Fort Cavazos",
                title: "Clear-out checklist & household ledger",
                description:
                  "O'Hara clears Fort Cavazos with a Before You Move checklist — final utilities, TMO pickup, and out-processing dates lined up against her Safe to Spend so nothing surprises the family budget.",
                mock: <MockClearingCavazos />,
              },
              {
                label: "Phase 02 · Advanced Recon",
                title: "BAH-friendly housing & certified daycares",
                description:
                  "Advanced Recon surfaces BAH-friendly housing, certified daycare spots, and TRICARE clinics near Fort Stewart — long before the moving truck ever rolls out of Texas.",
                mock: <MockAdvancedRecon />,
              },
              {
                label: "Phase 03 · Arrival",
                title: "Arrival at the Fort Stewart Welcome Center",
                description:
                  "By the time O'Hara pulls up to the Fort Stewart Welcome Center, her daycare spot is secured, her family's hospital is mapped, and life around her new duty station is already dialed in.",
                mock: <MockArrival />,
              },
            ]}
            footerCallout={
              <div
                className="mt-6 rounded-2xl p-4 flex items-start gap-3"
                style={{
                  background:
                    "linear-gradient(120deg, rgba(183,165,255,0.14) 0%, rgba(183,165,255,0.04) 100%)",
                  border: "1px solid rgba(183,165,255,0.35)",
                }}
                data-testid="story-card-2-privacy-guard"
              >
                <ShieldCheck size={18} className="text-[#B7A5FF] shrink-0 mt-0.5" />
                <div className="text-[13px] text-white leading-snug">
                  <span className="font-bold">Verified Bunker Security.</span>{" "}
                  <span className="text-[#C7D0DF]">
                    100% Foreground GPS only. No background tracking, ever.
                  </span>
                </div>
              </div>
            }
          />
        </div>
      </div>
    </section>
  );
};

export default MissionStories;
